<template>
  <div class="app-container" :class="{ 'sidebar-collapsed': sidebarCollapsed }">
    <Toast />
    <Toast group="expandable" position="top-right" :life="0">
      <template #message="slotProps">
        <ExpandableToast :message="slotProps.message" />
      </template>
    </Toast>
    <Toast group="document-created" position="top-right">
      <template #message="slotProps">
        <div class="toast-document-created">
          <i class="pi pi-check-circle toast-icon"></i>
          <div class="toast-content">
            <span class="toast-summary">{{ slotProps.message.summary }}</span>
            <p class="toast-detail">{{ slotProps.message.detail }}</p>
            <router-link
              v-if="slotProps.message.data?.id"
              :to="{ name: 'article-detail', params: { id: slotProps.message.data.id } }"
              class="toast-link"
            >
              <i class="pi pi-external-link"></i>
              Open document
            </router-link>
          </div>
        </div>
      </template>
    </Toast>
    <ConfirmDialog />
    
    <!-- Sidebar -->
    <aside v-if="authStore.isAuthenticated" class="app-sidebar">
      <div class="sidebar-header" role="button" :title="sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'" @click="toggleSidebar">
        <span v-if="!sidebarCollapsed" class="app-title">GT Database UI</span>
        <span v-else class="app-title-short">GT</span>
      </div>
      
      <nav class="sidebar-nav">
        <router-link 
          v-for="item in menuItems" 
          :key="item.name"
          v-tooltip.right="sidebarCollapsed ? item.label : null"
          :to="{ name: item.name }"
          class="nav-item"
          :class="{ active: isActiveRoute(item.name) }"
        >
          <i :class="item.icon"></i>
          <span v-if="!sidebarCollapsed" class="nav-label">{{ item.label }}</span>
        </router-link>
        
        <!-- Admin Section -->
        <div v-if="authStore.isAdmin" class="nav-section">
          <div v-if="!sidebarCollapsed" class="nav-section-title">Admin</div>
          <router-link 
            v-for="item in adminItems" 
            :key="item.name"
            v-tooltip.right="sidebarCollapsed ? item.label : null"
            :to="{ name: item.name }"
            class="nav-item"
            :class="{ active: isActiveRoute(item.name) }"
          >
            <i :class="item.icon"></i>
            <span v-if="!sidebarCollapsed" class="nav-label">{{ item.label }}</span>
          </router-link>
        </div>
      </nav>
      
      <div class="sidebar-footer">
        <div v-if="!sidebarCollapsed" class="user-info">
          <span class="user-name">{{ authStore.user?.name }}</span>
          <Tag :value="authStore.user?.role" size="small" />
        </div>
        <div v-else v-tooltip.right="authStore.user?.name" class="user-avatar">
          {{ authStore.user?.name?.charAt(0) || 'U' }}
        </div>
        <Button 
          icon="pi pi-sign-out" 
          :label="sidebarCollapsed ? '' : 'Logout'"
          severity="secondary"
          text
          size="small"
          class="logout-btn" 
          @click="logout"
        />
      </div>
    </aside>

    <!-- Main Content -->
    <main class="app-main" :class="{ 'with-sidebar': authStore.isAuthenticated }">
      <router-view v-slot="{ Component }">
        <transition name="fade" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
    </main>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/store/auth'
import { useToast } from 'primevue/usetoast'
import { useNotification } from '@/composables/useNotification'
import ExpandableToast from '@/components/common/ExpandableToast.vue'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const toast = useToast()
const { notifyError } = useNotification()

const sidebarCollapsed = ref(localStorage.getItem('sidebarCollapsed') === 'true')

/**
 * The OSI and Snapshot dashboards are deliberately absent.
 *
 * They listed records; the articles page lists articles and can now filter to
 * either side, which is the same question asked once instead of in three
 * places. Their pages still exist and are still linked from an article's tabs -
 * only the nav entries are gone.
 */
const menuItems = [
  { name: 'articles', label: 'Articles', icon: 'pi pi-sitemap' },
  { name: 'my-work', label: 'My recent work', icon: 'pi pi-history' },
  { name: 'upload', label: 'Upload', icon: 'pi pi-upload' }
]

const adminItems = [
  { name: 'admin-files', label: 'Files', icon: 'pi pi-folder-open' },
  { name: 'admin-users', label: 'Users', icon: 'pi pi-users' },
  { name: 'admin-import-export', label: 'Import/Export', icon: 'pi pi-download' }
]

const isActiveRoute = (name) => {
  return route.name === name || route.name?.startsWith(name + '-')
}

const toggleSidebar = () => {
  sidebarCollapsed.value = !sidebarCollapsed.value
  localStorage.setItem('sidebarCollapsed', sidebarCollapsed.value)
}

const logout = async () => {
  try {
    await authStore.logout()
    toast.add({ severity: 'success', summary: 'Logged out', life: 3000 })
    router.push({ name: 'login' })
  } catch (error) {
    notifyError('Logout failed', error)
  }
}
</script>

<style>
/* Sidebar Variables - prefixed to avoid PrimeVue conflicts */
:root {
  --gt-sidebar-width: 200px;
  --gt-sidebar-collapsed-width: 60px;
}

.app-container {
  min-height: 100vh;
  display: flex;
}

/* Sidebar Styles */
.app-sidebar {
  width: var(--gt-sidebar-width);
  background: #1e293b;
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  z-index: 1000;
  transition: width 0.3s ease;
  border-right: 1px solid #334155;
}

.sidebar-collapsed .app-sidebar {
  width: var(--gt-sidebar-collapsed-width);
}

.sidebar-header {
  padding: 1rem;
  border-bottom: 1px solid #334155;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 56px;
  cursor: pointer;
  transition: background 0.2s ease;
  overflow: hidden;
}

.sidebar-header:hover {
  background: #334155;
}

.app-title {
  font-size: 1rem;
  font-weight: 700;
  color: #60a5fa;
  white-space: nowrap;
}

.app-title-short {
  font-size: 1.1rem;
  font-weight: 700;
  color: #60a5fa;
  text-align: center;
}

.sidebar-nav {
  flex: 1;
  padding: 0.75rem 0;
  overflow-y: auto;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 1rem;
  color: #94a3b8;
  text-decoration: none;
  transition: all 0.2s ease;
  border-left: 3px solid transparent;
  margin: 0.125rem 0;
}

.sidebar-collapsed .nav-item {
  justify-content: center;
  padding: 0.625rem;
}

.nav-item:hover {
  background: #334155;
  color: #e2e8f0;
  text-decoration: none;
}

.nav-item.active {
  background: rgba(59, 130, 246, 0.2);
  color: #60a5fa;
  border-left-color: #60a5fa;
}

.nav-item i {
  font-size: 1.125rem;
  width: 22px;
  text-align: center;
  flex-shrink: 0;
}

.nav-label {
  white-space: nowrap;
  font-size: 0.9rem;
}

.nav-section {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #334155;
}

.nav-section-title {
  padding: 0.5rem 1rem;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  color: #94a3b8;
  letter-spacing: 0.05em;
}

.sidebar-footer {
  padding: 0.75rem;
  border-top: 1px solid #334155;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  background: #334155;
  border-radius: 6px;
}

.user-name {
  font-size: 0.85rem;
  color: #e2e8f0;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-avatar {
  width: 36px;
  height: 36px;
  background: #60a5fa;
  color: #1e293b;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.9rem;
  margin: 0 auto;
}

.logout-btn {
  width: 100%;
  justify-content: center;
}

.sidebar-collapsed .logout-btn :deep(.p-button-label) {
  display: none;
}

/* Main Content */
.app-main {
  flex: 1;
  padding: 1.5rem;
  height: 100vh;
  overflow: auto;
  background: #0f172a;
}

.app-main.with-sidebar {
  margin-left: var(--gt-sidebar-width);
  transition: margin-left 0.3s ease;
}

.sidebar-collapsed .app-main.with-sidebar {
  margin-left: var(--gt-sidebar-collapsed-width);
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Custom Toast for Document Created */
.toast-document-created {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.5rem;
}

.toast-document-created .toast-icon {
  font-size: 1.5rem;
  color: #10b981;
  margin-top: 0.125rem;
}

.toast-document-created .toast-content {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.toast-document-created .toast-summary {
  font-weight: 600;
  color: #e2e8f0;
  font-size: 0.95rem;
}

.toast-document-created .toast-detail {
  color: #94a3b8;
  font-size: 0.85rem;
  margin: 0;
}

.toast-document-created .toast-link {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  color: #60a5fa;
  font-size: 0.85rem;
  text-decoration: none;
  margin-top: 0.375rem;
  padding: 0.25rem 0.5rem;
  background: rgba(96, 165, 250, 0.1);
  border-radius: 4px;
  transition: all 0.2s;
}

.toast-document-created .toast-link:hover {
  background: rgba(96, 165, 250, 0.2);
  color: #93c5fd;
}

.toast-document-created .toast-link i {
  font-size: 0.75rem;
}
</style>
